import {t, Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isNotNull} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {parseDomainName} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {StyledBoxDiv} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {DomainModel, DomainValidityStatusesEnum} from '../../../models/api/domainModel';
import {TokensEditor} from '../editor/tokens/tokensEditor';
import {CreateFormInputDisplay} from './createFormInputDisplay';
import {EmailDomainToken} from './emailDomainToken';

/*
 * Constants.
 */

const delimiterRegexp = /[,;\t\n\s]+/;

// When the input is focused, the text that the user is currently typing counts as a token
// so we need to increase the minimum by one to check for a delimiter
const minTokensWithFocus = 2;
const minTokensWithoutFocus = 1;

/*
 * Types.
 */

interface EmailDomainInputProps {
  domains: ReadonlyArray<DomainModel>;
  onTextChange?: HandlerOf<string>;
  updateDomains: HandlerOf<ReadonlyArray<DomainModel>>;
  validateDomain: AsyncHandlerOf<DomainModel, DomainModel>;
}

/*
 * Style.
 */

const StyledTokensEditorWrapperDiv = styled.div`
  padding: 4px 6px;
  width: 100%;
`;

const StyledTokensEditor = styled(TokensEditor)`
  display: flex;
  align-items: center;
`;

const StyledExplanatoryTextDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  margin-top: 5px;
  line-height: 18px;

  &:not(:first-child) {
    display: none;
  }
`;

const StyledErrorDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  margin-top: 5px;
  line-height: 18px;
`;

/*
 * Component.
 */

export const EmailDomainsInput: FC<EmailDomainInputProps> = (props) => {
  const {domains, onTextChange: onCustomTextChange, updateDomains, validateDomain} = props;
  const [tokensEditorText, setTokensEditorText] = useState('');
  const [isValidating, setIsValidating] = useState<boolean>(false);

  const localize = useLocalize();

  useEffect(() => {
    // Skip validating if we're currently validating or there are no domains to check
    if (
      isValidating ||
      domains.length === 0 ||
      !domains.some((domain) => domain.validityStatus === DomainValidityStatusesEnum.TO_BE_CHECKED)
    ) {
      return;
    }

    const validateDomains = async () => {
      setIsValidating(true);

      // Make requests to check each domain that needs to be checked
      // Otherwise just resolve to the domain
      const updatedDomains: Array<DomainModel> = await Promise.all(
        domains.map((domain) =>
          domain.validityStatus === DomainValidityStatusesEnum.TO_BE_CHECKED
            ? validateDomain(domain)
            : Promise.resolve(domain),
        ),
      );

      updateDomains(updatedDomains);
      setIsValidating(false);
    };

    validateDomains();
  }, [domains, isValidating, updateDomains, validateDomain]);

  const addDomainsFromText = (inputText: string, hasFocus: boolean) => {
    // Split text to check if we need to look for domains
    const tokens = inputText.split(delimiterRegexp);

    // If we didn't find a delimiter, return early
    const minTokens = hasFocus ? minTokensWithFocus : minTokensWithoutFocus;
    if (tokens.length < minTokens) {
      setTokensEditorText(inputText);
      return;
    }

    // Parse the tokens into valid domains
    const {newDomainNames, invalidTokens} = parseDomainNames(tokens, domains);

    // Add the new domains
    const newDomains = newDomainNames.map((newDomainName) => ({
      name: newDomainName,
      validityStatus: DomainValidityStatusesEnum.TO_BE_CHECKED,
    }));
    updateDomains([...domains, ...newDomains]);
    if (onCustomTextChange) {
      onCustomTextChange(invalidTokens.length === 0 ? '' : inputText);
    }

    // Keep the invalid tokens as text in the input
    setTokensEditorText(invalidTokens.join(''));
  };

  const onRemoveTokensClick = (tokenIndexesToRemove: ReadonlyArray<number>) => {
    const filteredDomains = domains.filter((_d, index) => !tokenIndexesToRemove.includes(index));
    updateDomains(filteredDomains);
  };

  const onTextChange = (text: string) => {
    if (onCustomTextChange) {
      onCustomTextChange(text.trim());
    }
    addDomainsFromText(text, true);
  };

  const onBlur = () => {
    addDomainsFromText(tokensEditorText, false);
  };

  return (
    <>
      <CreateFormInputDisplay
        title={<Trans id="create-form-email-domains">Email domains</Trans>}
        input={
          <StyledBoxDiv $size={VisualSizesEnum.MEDIUM} $noMargin>
            <StyledTokensEditorWrapperDiv>
              <StyledTokensEditor
                tokenIds={domains.map((d) => d.name)}
                renderToken={(index: number, isSelected: boolean) => (
                  <EmailDomainToken domain={domains[index]} isSelected={isSelected} />
                )}
                onRemoveTokensClick={onRemoveTokensClick}
                text={tokensEditorText}
                onTextChange={onTextChange}
                onBlur={onBlur}
                placeholder={localize(t('create-form-automatic-association-placeholder')`Add domain`)}
              />
            </StyledTokensEditorWrapperDiv>
          </StyledBoxDiv>
        }
      />
      <div>
        {maybeRenderError(domains)}
        <StyledExplanatoryTextDiv>
          <Trans id="create-form-automatic-association-description">
            e.g. “front.com”. Contacts with matching email domains will be automatically associated with this
            account.
          </Trans>
        </StyledExplanatoryTextDiv>
      </div>
    </>
  );
};

/*
 * Helpers.
 */

function parseDomainNames(tokens: Array<string>, existingDomains: ReadonlyArray<DomainModel>) {
  const newDomainNames: Array<string> = _(tokens)
    .map((token) => {
      // Try to parse the token as a domain name
      const domainName = parseDomainName(token);
      if (!domainName) {
        return null;
      }

      // Check if we already have the domain
      if (existingDomains.some((domain) => domain.name.toLowerCase() === domainName.toLowerCase())) {
        return null;
      }

      // Otherwise, keep it
      return domainName;
    })
    .filter(isNotNull)
    .uniq()
    .value();

  const invalidTokens = tokens.filter((_t, index) => !newDomainNames[index]);

  return {
    newDomainNames,
    invalidTokens,
  };
}

function maybeRenderError(domains: ReadonlyArray<DomainModel>) {
  const hasConflictDomains = domains.some(
    (domain) => domain.validityStatus === DomainValidityStatusesEnum.INVALID_CONFLICT,
  );

  const hasCommonDomains = domains.some(
    (domain) => domain.validityStatus === DomainValidityStatusesEnum.INVALID_COMMON,
  );
  if (!hasConflictDomains && !hasCommonDomains) {
    return null;
  }

  // TODO: Add link to open conflicting account in a pop out window
  return (
    <>
      {hasConflictDomains && (
        <StyledErrorDiv>
          <Trans id="create-form-domain-taken-error">Domain already taken by another account.</Trans>
        </StyledErrorDiv>
      )}
      {hasCommonDomains && (
        <StyledErrorDiv>
          <Trans id="create-form-domain-common-error">
            Common email domains cannot be used with accounts.
          </Trans>
        </StyledErrorDiv>
      )}
    </>
  );
}
